import React, { useCallback } from 'react';
import { Radio } from 'antd';
import LookupField from '../../../components/LookupField';
import type { FP620 } from '../../../services/pwp255Api';
import { dateToInput, inputToDate } from '../helpers';

interface Tab1BasicInfoProps {
  formData: Partial<FP620>;
  isEditing: boolean;
  mode: 'view' | 'add' | 'edit';
  onChange: (field: keyof FP620, value: string | number | null) => void;
}

const Tab1BasicInfo: React.FC<Tab1BasicInfoProps> = ({ formData, isEditing, mode, onChange }) => {
  const ro = !isEditing;
  const isEditMode = mode === 'edit';

  const handleScNoConfirm = useCallback((code: string) => {
    onChange('fp620_10', code);
  }, [onChange]);

  const handleCustomerConfirm = useCallback((code: string) => {
    onChange('fp620_17', code);
  }, [onChange]);

  const handleProductConfirm = useCallback((code: string) => {
    onChange('fp620_29', code);
  }, [onChange]);

  return (
    <div className="pwp255-tab1">
      {/* FP620_01 - 單據號碼 (readOnly always) */}
      <div className="pwp255-field-row">
        <label>單據號碼</label>
        <input
          type="text"
          value={formData.fp620_01 || ''}
          readOnly
          disabled
          className="pwp255-input"
          placeholder="自動產生"
        />
      </div>

      {/* FP620_11 - 日期 */}
      <div className="pwp255-field-row">
        <label><span className="required">*</span>日期</label>
        <input
          type="date"
          value={dateToInput(formData.fp620_11 ?? null)}
          readOnly={ro}
          onChange={e => onChange('fp620_11', inputToDate(e.target.value))}
          className="pwp255-input"
        />
      </div>

      {/* FP620_10 - S/C NO. (DblClick lookup, disabled in edit mode per BR-004/013) */}
      <div className="pwp255-field-row">
        <label><span className="required">*</span>S/C NO.</label>
        <LookupField
          lookupType="salesContract"
          value={formData.fp620_10 || ''}
          onChange={v => onChange('fp620_10', v)}
          onCodeConfirm={handleScNoConfirm}
          disabled={ro || isEditMode}
          maxLength={6}
          style={{ width: 200 }}
        />
      </div>

      {/* FP620_17 - 客戶代號 (DblClick lookup) */}
      <div className="pwp255-field-row">
        <label>客戶代號</label>
        <LookupField
          lookupType="packingCustomer"
          value={formData.fp620_17 || ''}
          onChange={v => onChange('fp620_17', v)}
          onCodeConfirm={handleCustomerConfirm}
          disabled={ro}
          maxLength={12}
          style={{ width: 200 }}
        />
      </div>

      {/* FP620_29 - 產品代號 (DblClick lookup, auto-fill FP620_14) */}
      <div className="pwp255-field-row">
        <label>產品代號</label>
        <LookupField
          lookupType="product"
          value={formData.fp620_29 || ''}
          onChange={v => onChange('fp620_29', v)}
          onCodeConfirm={handleProductConfirm}
          disabled={ro}
          maxLength={12}
          style={{ width: 200 }}
        />
      </div>

      {/* FP620_13 - SHIPPING MARK (editbox/textarea, readOnly per spec) */}
      <div className="pwp255-field-row">
        <label>SHIPPING MARK</label>
        <textarea
          value={formData.fp620_13 || ''}
          readOnly
          disabled
          className="pwp255-textarea"
          rows={3}
        />
      </div>

      {/* FP620_141 - DESCRIPTION OF GOODS (editbox/textarea, editable) */}
      <div className="pwp255-field-row">
        <label><span className="required">*</span>DESCRIPTION OF GOODS</label>
        <textarea
          value={formData.fp620_141 || ''}
          readOnly={ro}
          onChange={e => onChange('fp620_141', e.target.value)}
          className="pwp255-textarea"
          rows={3}
        />
      </div>

      {/* FP620_15 - CONTAINER NO. (textbox, readOnly per spec) */}
      <div className="pwp255-field-row">
        <label>CONTAINER NO.</label>
        <input
          type="text"
          value={formData.fp620_15 || ''}
          readOnly
          disabled
          maxLength={20}
          className="pwp255-input"
        />
      </div>

      {/* FP620_16 - 材積 (textbox, readOnly per spec) */}
      <div className="pwp255-field-row">
        <label>材積</label>
        <input
          type="text"
          value={formData.fp620_16 || ''}
          readOnly
          disabled
          maxLength={50}
          className="pwp255-input"
        />
      </div>

      {/* FP620_25 - 出貨單位 (Radio.Group, disabled in edit mode per BR-004) */}
      <div className="pwp255-field-row">
        <label><span className="required">*</span>出貨單位</label>
        <Radio.Group
          value={formData.fp620_25 || 1}
          disabled={ro || isEditMode}
          onChange={e => onChange('fp620_25', e.target.value)}
        >
          <Radio value={1}>YDS</Radio>
          <Radio value={2}>MTS</Radio>
          <Radio value={3}>PCS</Radio>
        </Radio.Group>
      </div>

      {/* FP620_28 - 針織/平織 (Radio.Group, disabled in edit mode per BR-004) */}
      <div className="pwp255-field-row">
        <label>
          {formData.fp620_25 !== 3 && <span className="required">*</span>}
          針織/平織
        </label>
        <Radio.Group
          value={formData.fp620_28 || 1}
          disabled={ro || isEditMode}
          onChange={e => onChange('fp620_28', e.target.value)}
        >
          <Radio value={1}>針織</Radio>
          <Radio value={2}>平織</Radio>
        </Radio.Group>
      </div>

      {/* FP620_22 - MTS 換算率 (visible only when unit != 3) */}
      {formData.fp620_25 !== 3 && (
        <div className="pwp255-field-row">
          <label>MTS 換算率</label>
          <input
            type="number"
            step="0.0001"
            value={formData.fp620_22 ?? 0.9144}
            readOnly={ro}
            onChange={e => onChange('fp620_22', parseFloat(e.target.value) || 0)}
            className="pwp255-input"
            style={{ width: 120 }}
          />
        </div>
      )}

      {/* FP620_23 - N.W. 換算率 */}
      <div className="pwp255-field-row">
        <label>N.W. 換算率</label>
        <input
          type="number"
          step="0.0001"
          value={formData.fp620_23 ?? 0}
          readOnly={ro}
          onChange={e => onChange('fp620_23', parseFloat(e.target.value) || 0)}
          className="pwp255-input"
          style={{ width: 120 }}
        />
      </div>

      {/* FP620_24 - G.W. 增量 */}
      <div className="pwp255-field-row">
        <label>G.W. 增量</label>
        <input
          type="number"
          step="0.0001"
          value={formData.fp620_24 ?? 0}
          readOnly={ro}
          onChange={e => onChange('fp620_24', parseFloat(e.target.value) || 0)}
          className="pwp255-input"
          style={{ width: 120 }}
        />
      </div>
    </div>
  );
};

export default React.memo(Tab1BasicInfo);
